import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Stack,
    Alert,
    MenuItem,
    TextField,
} from '@mui/material';

import { useNavigateParams } from '../../../hooks/useNavigateParams';
import Input from '../../form/Input';
import SelectEmpresa from '../../form/SelectEmpresa';
import DataList from '../../form/DataList';

function Form({
    dados,
    setDados,
    handleSubmit,
    caminhoVoltar,
    OptionsEmpresa,
    editando,
    carregando,
    falhaCarregamento,
    opcoesConfiguracao = {},
    onAlterarConfiguracao,
    onBuscarOpcoesConfiguracao,
    transformacoesPorEmpresa = {},
    onAlterarTransformacao,
}) {
    const navigate = useNavigateParams();

    function handleChange(e) {
        const { name, value } = e.target;

        setDados((anterior) => ({
            ...anterior,
            [name]: name === 'codigo' ? value.toUpperCase() : value,
        }));
    }

    function handleEmpresaPermitida(empresaSelecionadaId, checked) {
        const id = Number(empresaSelecionadaId);
        const empresaPrincipalId = Number(dados.empresaId);

        if (id === empresaPrincipalId && !checked) return;

        setDados((anterior) => {
            const atuais = Array.isArray(anterior.empresaIds) ? anterior.empresaIds.map(Number) : [];
            const empresaIds = checked ? [...new Set([...atuais, id])] : atuais.filter((item) => item !== id);

            if (!empresaIds.includes(empresaPrincipalId)) {
                empresaIds.push(empresaPrincipalId);
            }

            return { ...anterior, empresaIds };
        });
    }

    function submit(e) {
        e.preventDefault();
        handleSubmit(dados);
    }

    return (
        <form onSubmit={submit} autoComplete="off">
            <h1>{editando ? 'CONFIGURAÇÕES DA CREDENCIAL' : 'CREDENCIAL DE INTEGRAÇÃO PDV'}</h1>
            <div>
                {!editando && (
                    <SelectEmpresa
                        text="Selecione a Empresa"
                        name="empresaId"
                        options={OptionsEmpresa}
                        handleOnChange={handleChange}
                        value={dados.empresaId || ''}
                        required
                        notOptNull={true}
                    />
                )}

                {!editando && (
                    <>
                        <Input
                            text="Código"
                            type="text"
                            name="codigo"
                            placeholder="Ex.: PDV-EMPRESA-15"
                            title="Identificação interna da credencial"
                            handleOnChange={handleChange}
                            value={dados.codigo || ''}
                            maxLength={50}
                            required={true}
                        />

                        <Input
                            text="Descrição"
                            type="text"
                            name="descricao"
                            placeholder="Ex.: PDV da empresa 15"
                            title="Descrição do sistema ou ponto de venda que utilizará a credencial"
                            handleOnChange={handleChange}
                            value={dados.descricao || ''}
                            maxLength={255}
                            required={true}
                        />
                    </>
                )}

                {editando && (
                    <FormControl component="fieldset" disabled={carregando || falhaCarregamento} sx={{ my: 2 }}>
                        <FormLabel component="legend">Empresas que a credencial pode consultar</FormLabel>
                        <FormGroup>
                            {OptionsEmpresa.map((empresa) => {
                                const id = Number(empresa.id);
                                const principal = id === Number(dados.empresaId);
                                const checked = (dados.empresaIds || []).map(Number).includes(id);

                                return (
                                    <FormControlLabel
                                        key={empresa.uuid || empresa.id}
                                        control={
                                            <Checkbox
                                                checked={checked}
                                                disabled={carregando || principal}
                                                onChange={(event) => handleEmpresaPermitida(id, event.target.checked)}
                                            />
                                        }
                                        label={`${empresa.nomeFantasia || empresa.razaoSocial}${principal ? ' (principal)' : ''}`}
                                    />
                                );
                            })}
                        </FormGroup>
                        <FormHelperText>A empresa principal é obrigatória e não pode ser removida.</FormHelperText>
                    </FormControl>
                )}

                {editando && (
                    <Stack spacing={2} sx={{ my: 2 }}>
                        {OptionsEmpresa.filter((empresa) =>
                            (dados.empresaIds || []).map(Number).includes(Number(empresa.id))
                        ).map((empresa) => {
                            const empresaId = Number(empresa.id);
                            const configuracao =
                                (dados.configuracoes || []).find((item) => Number(item.empresaId) === empresaId) || {};

                            const consulta = transformacoesPorEmpresa[empresaId];

                            const consultaAtual =
                                Number(consulta?.operacaoId || 0) === Number(configuracao.operacaoRecebimentoId || 0);

                            const opcoesTransformacao = consultaAtual ? consulta?.opcoes || [] : [];

                            const bloqueado =
                                carregando ||
                                falhaCarregamento ||
                                !configuracao.operacaoRecebimentoId ||
                                !consultaAtual ||
                                !consulta ||
                                consulta.carregando ||
                                Boolean(consulta.erro);

                            return (
                                <fieldset key={empresaId} disabled={carregando || falhaCarregamento}>
                                    <legend>{empresa.nomeFantasia || empresa.razaoSocial}</legend>

                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                        <DataList
                                            text="Usuário técnico"
                                            title="Usuário responsável pelos registros recebidos"
                                            name={`integracao-${empresaId}-usuario`}
                                            inputWidth="350px"
                                            displayField="texto"
                                            filteredOptions={opcoesConfiguracao[`${empresaId}:usuario`] || []}
                                            value={configuracao.usuarioTexto || ''}
                                            handleOnChange={(event) =>
                                                onAlterarConfiguracao(empresaId, 'usuario', event.target.value)
                                            }
                                            handleOnFocus={() => onBuscarOpcoesConfiguracao(empresaId, 'usuario', '')}
                                        />

                                        <DataList
                                            text="Operação de recebimento"
                                            title="Operação inicial dos movimentos recebidos"
                                            name={`integracao-${empresaId}-operacao`}
                                            inputWidth="350px"
                                            displayField="texto"
                                            filteredOptions={opcoesConfiguracao[`${empresaId}:operacao`] || []}
                                            value={configuracao.operacaoTexto || ''}
                                            handleOnChange={(event) =>
                                                onAlterarConfiguracao(empresaId, 'operacao', event.target.value)
                                            }
                                            handleOnFocus={() => onBuscarOpcoesConfiguracao(empresaId, 'operacao', '')}
                                        />
                                    </Stack>

                                    {consultaAtual && consulta?.erro && (
                                        <Alert severity="error" sx={{ my: 1 }}>
                                            {consulta.erro}
                                        </Alert>
                                    )}

                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
                                        {[
                                            {
                                                campo: 'transformacaoFiscalId',
                                                label: 'Transformação fiscal',
                                                fiscal: true,
                                            },
                                            {
                                                campo: 'transformacaoNaoFiscalId',
                                                label: 'Transformação não fiscal',
                                                fiscal: false,
                                            },
                                        ].map(({ campo, label, fiscal }) => {
                                            const opcoes = opcoesTransformacao.filter((item) => item.contabil === fiscal);

                                            const valor = configuracao[campo] || '';
                                            const disponivel = opcoes.some((item) => Number(item.id) === Number(valor));

                                            return (
                                                <TextField
                                                    key={campo}
                                                    select
                                                    label={label}
                                                    value={valor}
                                                    disabled={bloqueado}
                                                    onChange={(event) =>
                                                        onAlterarTransformacao(empresaId, campo, event.target.value)
                                                    }
                                                    sx={{ width: 350, maxWidth: '100%' }}
                                                    helperText={
                                                        consulta?.carregando
                                                            ? 'Carregando transformações...'
                                                            : 'Configuração vinculada à operação de recebimento.'
                                                    }
                                                >
                                                    <MenuItem value="">Não configurada</MenuItem>

                                                    {valor && !disponivel && (
                                                        <MenuItem value={valor} disabled>
                                                            {consulta?.carregando
                                                                ? 'Carregando seleção...'
                                                                : `Transformação ${valor} indisponível`}
                                                        </MenuItem>
                                                    )}

                                                    {opcoes.map((item) => (
                                                        <MenuItem key={item.id} value={item.id}>
                                                            {item.texto}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            );
                                        })}
                                    </Stack>
                                    <FormHelperText>
                                        Selecione usuário e operação para configurar o recebimento. Limpar ambos remove essa
                                        configuração.
                                    </FormHelperText>
                                </fieldset>
                            );
                        })}
                    </Stack>
                )}
            </div>

            <Stack direction="row" spacing={1}>
                <Button variant="contained" color="secondary" onClick={() => navigate(caminhoVoltar)}>
                    Voltar
                </Button>
                <Button type="submit" variant="contained" color="success" disabled={carregando || falhaCarregamento}>
                    {editando ? 'Salvar configurações' : 'Criar credencial'}
                </Button>
            </Stack>
        </form>
    );
}

export default Form;
